import { Stack } from "@mui/material";
import { partitionReviewersAndSigners } from "components/Submission/helpers/documentHelpers";
import { RequirementCitations } from "components/Submission/RequirementCitations";
import { ScoutCitationExplainer } from "components/Submission/ScoutCitationExplainer";
import { SubmissionReadOnlySummary } from "components/Submission/SubmissionReadOnlySummary";
import { getWasteSubmissionFormName } from "encamp-shared/src/submissions/wasteSubmissionForms";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { SubmissionStepType, SubmissionType } from "generated-graphql/graphql";
import { useAcknowledgeUnderstandStep } from "hooks/useAcknowledgeUnderstandStep";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useWasteStepper } from "hooks/useWasteStepper";
import { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { V2DisplayOnlyField as DisplayOnlyField } from "routes/Customer/Facility/ProfileV2/V2DisplayOnlyField";

import { DetailsCard } from "../DetailsCard";
import { StepHeader } from "../StepHeader";
import { StepSubHeader } from "../StepSubHeader";
import { NotificationDeadlines } from "./NotificationDeadlines";
import { StepContinueFooter } from "./StepContinueFooter";

/**
 * Understand step (document forms): what this submission is, why it is
 * required, its details, and who reviews and signs it. Rendered entirely from
 * the shell query — the step fetches no data of its own. First step of the
 * flow, so no Back.
 */
export function UnderstandStep({ submissionId }: { submissionId: string; facilityId: string }) {
  const { tenantId = "" } = useParams();
  const { submission, canEdit } = useWasteStepper();
  const { user, isStaff } = useCurrentUser();
  const { acknowledgeStep, loading: acknowledging } = useAcknowledgeUnderstandStep(submissionId);

  // Reviewers/signers (non-editing customers) acknowledge this step once on their
  // own row; staff/editors record shared step progress through the default footer.
  const reviewers = useMemo(() => submission?.reviewers ?? [], [submission]);
  const myReviewer = reviewers.find((r) => r.user.id === user?.id);
  const isReviewerView = !canEdit && !!myReviewer;
  const hasAcknowledged = !!myReviewer?.acknowledgedAt;

  const handleReviewerContinue = useCallback(async () => {
    if (!hasAcknowledged) await acknowledgeStep();
  }, [hasAcknowledged, acknowledgeStep]);

  // Reviewers continue straight to where they act, skipping the read-only steps.
  const steps = (submission?.stepperSteps ?? []).map(({ step }) => step);
  const reviewerNextStep = steps.includes(SubmissionStepType.ReviewAndSign)
    ? SubmissionStepType.ReviewAndSign
    : undefined;

  // Named forms (8700-13) show their EPA form name; one-off combos fall back
  // to the submission type.
  const formName =
    getWasteSubmissionFormName(submission?.submissionType, submission?.submissionMethod) ??
    (submission?.submissionType ? prettyPrintEnumValue(submission.submissionType) : "");
  const citations = submission?.requirementCitations ?? [];

  const { signer, reviewers: nonSignerReviewers } = useMemo(
    () => partitionReviewersAndSigners(reviewers),
    [reviewers]
  );

  return (
    <Stack spacing={3}>
      <StepHeader
        title="Understand"
        description="What this submission is, why it is required, and who is involved."
      />
      <DisplayOnlyField label="Submission Type" text={formName} />
      {isStaff &&
        submission?.submissionType === SubmissionType.GeneratorStatusChangeNotification && (
          <NotificationDeadlines submissionId={submissionId} />
        )}
      {citations.length > 0 && (
        <Stack spacing={1}>
          <StepSubHeader
            title="Why this is required"
            titleAdornment={
              <ScoutCitationExplainer
                submissionId={submissionId}
                citations={citations}
                formName={formName}
              />
            }
          />
          <RequirementCitations citations={citations} />
        </Stack>
      )}
      <DetailsCard />
      <Stack spacing={1}>
        <StepSubHeader title="Reviewers & Signer" />
        {/* Submission Details owns everything about the submission itself;
            this summary is only the people acting on it. */}
        <SubmissionReadOnlySummary
          tenantId={tenantId}
          isStaff={isStaff}
          facilities={[]}
          reviewers={nonSignerReviewers}
          signer={signer}
          manifest={submission?.processedManifestExceptionReport}
          showTitle={false}
          showDueDate={false}
          showStaffAssignee={false}
          showNotes={false}
          showRecipient={false}
        />
      </Stack>
      {isReviewerView ? (
        <StepContinueFooter
          submissionId={submissionId}
          step={SubmissionStepType.Understand}
          label="Continue"
          canContinue
          onContinue={handleReviewerContinue}
          nextStep={reviewerNextStep}
          loading={acknowledging}
        />
      ) : (
        <StepContinueFooter
          submissionId={submissionId}
          step={SubmissionStepType.Understand}
          label="Continue"
        />
      )}
    </Stack>
  );
}
